import { AxiosError } from 'axios';
import React from 'react';
import { Link } from 'react-aria-components';
import { useForm } from 'react-hook-form';

import { Button } from '#/shared/components/Button';
import { FormControlTextArea, FormControlTextInput } from '#/shared/components/FormControl';
import { FormControlMultiTextInput } from '#/shared/components/FormControl/FormControlMultiTextInput';
import { MaterialIcon } from '#/shared/components/MaterialIcon';
import { ModalContent } from '#/shared/components/Modal';
import { appConfig } from '#/shared/config/AppConfig';
import { useAnalytics } from '#/shared/hooks/useAnalytics';
import { useMonitoring } from '#/shared/hooks/useMonitoring';
import { usePageStateStore } from '#/shared/stores/pageStateStore';
import { useTimesheetStore } from '#/shared/stores/timesheetStore';
import { validateEmail, validateRequiredField } from '#/shared/utils/formHelper';

import { mapForwardRequestFormValuesToForwardRequestApiData } from '../adaptors/mapForwardRequestFormValuesToForwardRequestApiData';
import { useForwardRequestMutation } from '../hooks/useForwardRequestMutation';
import { ForwardRequestApiErrorResponseData } from '../types/ForwardRequestApi';
import { ForwardRequestFormValues } from '../types/ForwardRequestFormValues';

interface ForwardRequestFormOldProps {
  onModalClose: () => void;
}
// TODO: PPL-2239-timesheet-actions-selection cleanup - Delete this component
export function ForwardRequestFormOld({ onModalClose }: Readonly<ForwardRequestFormOldProps>) {
  const { noticeError } = useMonitoring();
  const { track } = useAnalytics();
  const timesheetId = useTimesheetStore(state => state.timesheet?.timesheetId ?? '');
  const setPageState = usePageStateStore(state => state.setPageState);

  const { handleSubmit, control, register } = useForm<ForwardRequestFormValues>({
    defaultValues: {
      requesteeName: '',
      requesteeEmail: '',
      forwarderName: '',
      forwarderEmails: [{ email: '' }],
      forwarderMessage: '',
    },
    mode: 'onSubmit',
  });

  const forwardRequest = useForwardRequestMutation();
  const [isSubmissionError, setIsSubmissionError] = React.useState(false);
  const [isSubmissionPending, setIsSubmissionPending] = React.useState(false);

  const onSubmit = async (formData: ForwardRequestFormValues) => {
    setIsSubmissionError(false);
    setIsSubmissionPending(true);

    try {
      const data = mapForwardRequestFormValuesToForwardRequestApiData(formData);

      await forwardRequest.mutateAsync({ data });

      track('Online approval web - Forward request success', {
        Timesheet_ID: timesheetId,
      });

      setIsSubmissionPending(false);
      onModalClose();
      setPageState('forwarding-success');
    } catch (error) {
      setIsSubmissionError(true);
      setIsSubmissionPending(false);

      track('Online approval web - Forward request failure', {
        Timesheet_ID: timesheetId,
      });

      const expectedError = error as AxiosError<ForwardRequestApiErrorResponseData>;
      noticeError(new Error('Online approval request forwarding has failed'), {
        originalMessage: expectedError?.message,
        originalData: expectedError?.response?.data,
        originalError: expectedError,
        timesheetId,
      });
    }
  };

  return (
    <ModalContent title={'Forward approval request'} onCancel={onModalClose} onSubmit={handleSubmit(onSubmit)}>
      {/* overflow-y-auto so the middle section scrolls */}
      <div className="gap-32 flex flex-col overflow-y-auto px-24 pb-4">
        <div className="gap-16 flex flex-col">
          <FormControlTextInput
            label="New approver's full name"
            placeholder="Enter text"
            name={'requesteeName'}
            type="text"
            control={control}
            rules={{
              validate: {
                required: validateRequiredField,
              },
            }}
            errorLeadingIconName={'error'}
          />

          <FormControlTextInput
            label="New approver's email address"
            description="By forwarding, you confirm that you are authorised to share this email address."
            descriptionPosition="above"
            placeholder="Enter email"
            name={'requesteeEmail'}
            type="text"
            control={control}
            rules={{
              validate: {
                email: validateEmail,
                required: validateRequiredField,
              },
            }}
            errorLeadingIconName={'error'}
          />

          <FormControlTextInput
            label="Your name"
            description="Used only to inform the new approver of who forwarded the request."
            descriptionPosition="above"
            placeholder="Enter text"
            name={'forwarderName'}
            type="text"
            control={control}
            rules={{
              validate: {
                required: validateRequiredField,
              },
            }}
            errorLeadingIconName={'error'}
          />
        </div>

        <div className="gap-16 flex flex-col">
          <h3 className="text-18 text-left font-700">Optional</h3>

          <FormControlMultiTextInput
            name="forwarderEmails"
            control={control}
            register={register}
            validate={validateEmail}
            label="Email address (optional)"
            description="If you'd like to receive a copy of the approved timesheet."
            placeholder="Enter email"
            addFieldButtonLabel="Add another email address"
            type="text"
            fieldKey="email"
            minFields={1}
            maxFields={5}
          />

          <FormControlTextArea
            label="Message for new approver (optional)"
            placeholder="Enter text"
            name={'forwarderMessage'}
            control={control}
            maxLength={500}
            rules={{
              maxLength: { value: 500, message: 'Maximum allowed characters is 500' },
            }}
            errorLeadingIconName={'error'}
          />
        </div>
      </div>

      {isSubmissionError && (
        <div className="text-14 text-error text-left flex gap-4 px-24">
          <MaterialIcon iconName={'error'} size={'sm'} />
          <p>
            Sorry, unable to forward approval request. Please try again later or forward the approval request email
            directly. Contact{' '}
            <Link
              href={`mailto:${appConfig.feedback.email}`}
              className="text-primary hover:text-primary-shade focus:text-primary-shade hover:underline"
            >
              {appConfig.feedback.email}
            </Link>{' '}
            if the issue persists.
          </p>
        </div>
      )}

      <div className="px-24">
        <Button
          type="submit"
          size="medium"
          className={'w-full'}
          disabled={isSubmissionPending}
          isLoading={isSubmissionPending}
        >
          Forward approval request
        </Button>
      </div>
    </ModalContent>
  );
}
